import React, { useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';

import ChatPanel from '../shared/HelpWidget/ChatPanel';
import SupportRequestDialog from '../shared/HelpWidget/SupportRequestDialog';
import HelpVideosDialog from '../shared/HelpWidget/HelpVideosDialog';
import useHelpChat from '../shared/HelpWidget/useHelpChat';
import { normalizeLanguage } from '../../utils/languages';
import { SupportContacts, guidesBase } from './supportContacts';

export { SUPPORT_EMAIL, SUPPORT_PHONE } from './supportContacts';

const GUIDE_TOPICS = [
  { slug: 'tyomaaraykset', label: 'supportPage.topicWorkOrders' },
  { slug: 'laskutus', label: 'supportPage.topicInvoicing' },
  { slug: 'kalenteri', label: 'supportPage.topicCalendar' },
  { slug: 'rengashotelli', label: 'supportPage.topicTireHotel' },
  { slug: 'asetukset', label: 'supportPage.topicSettings' },
];

function SectionCard({ icon, title, lead, children, testId }) {
  return (
    <Paper
      elevation={0}
      data-testid={testId}
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '16px', border: '1px solid #e2e8f0' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            bgcolor: '#ebf4ff',
            color: 'secondary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          {title}
        </Typography>
      </Box>
      {lead && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {lead}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

/**
 * "Tuki ja ohjeet": one place for help, ordered so the user helps themselves first
 * (Verstaani avustaja, then the guides) and reaches customer service last.
 */
function SupportView() {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.language);
  const { messages, loading, sendMessage, newChat } = useHelpChat();
  // null = plain form; an object = prefilled from a chat message the assistant could not answer
  const [supportPrefill, setSupportPrefill] = useState(null);
  const [supportDialogOpen, setSupportDialogOpen] = useState(false);
  const [videosDialogOpen, setVideosDialogOpen] = useState(false);
  const base = guidesBase(language);

  const openSupport = (prefill = null) => {
    setSupportPrefill(prefill);
    setSupportDialogOpen(true);
  };

  return (
    <Box data-testid="support-page" sx={{ maxWidth: 1200 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
        {t('supportPage.title')}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, maxWidth: 720 }}>
        {t('supportPage.lead')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          // minmax(0, ...) on xs too: a plain 1fr track grows to the chat's min-content and overflows on phones
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 7fr) minmax(0, 5fr)' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          testId="support-assistant"
          icon={<SmartToyOutlinedIcon fontSize="small" />}
          title={t('supportPage.assistantTitle')}
          lead={t('supportPage.assistantLead')}
        >
          <Box sx={{ height: { xs: '65vh', md: 560 }, minHeight: 420 }}>
            <ChatPanel
              variant="embedded"
              messages={messages}
              loading={loading}
              onSendMessage={sendMessage}
              onSuggestionClick={sendMessage}
              onNewChat={newChat}
              onOpenSupport={openSupport}
              onOpenVideos={() => setVideosDialogOpen(true)}
            />
          </Box>
        </SectionCard>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionCard
            testId="support-guides"
            icon={<MenuBookOutlinedIcon fontSize="small" />}
            title={t('supportPage.guidesTitle')}
            lead={t('supportPage.guidesLead')}
          >
            <Button
              variant="contained"
              href={base}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon />}
              data-testid="support-open-guides"
              sx={{ borderRadius: '10px', bgcolor: 'secondary.main', mb: 2 }}
            >
              {t('supportPage.openGuides')}
            </Button>
            {language === 'en' && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: -1, mb: 2 }}>
                {t('supportPage.guidesFinnishOnly')}
              </Typography>
            )}
            <Typography
              variant="overline"
              sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5, mb: 0.75 }}
            >
              {t('supportPage.popularTopics')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {GUIDE_TOPICS.map((topic) => (
                <Button
                  key={topic.slug}
                  variant="outlined"
                  size="small"
                  href={`${base}${topic.slug}/`}
                  target="_blank"
                  rel="noopener"
                  sx={{ borderRadius: '999px', textTransform: 'none', borderColor: '#e2e8f0', color: 'text.primary' }}
                >
                  {t(topic.label)}
                </Button>
              ))}
            </Box>
          </SectionCard>

          <SectionCard
            testId="support-customer-service"
            icon={<SupportAgentOutlinedIcon fontSize="small" />}
            title={t('supportPage.customerServiceTitle')}
            lead={t('supportPage.customerServiceLead')}
          >
            <Button
              variant="outlined"
              onClick={() => openSupport()}
              data-testid="support-request-button"
              sx={{ borderRadius: '10px', mb: 2 }}
            >
              {t('Submit support request')}
            </Button>
            <SupportContacts />
          </SectionCard>
        </Box>
      </Box>

      <SupportRequestDialog
        open={supportDialogOpen}
        prefill={supportPrefill}
        onClose={() => setSupportDialogOpen(false)}
      />
      <HelpVideosDialog open={videosDialogOpen} onClose={() => setVideosDialogOpen(false)} />
    </Box>
  );
}

export default SupportView;
